import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getProspects, createProspect, deleteProspect } from '../services/api';
import { exportTableToPDF } from '../utils/pdfExport';
import EmptyState from '../components/EmptyState';

const statusColors: Record<string, string> = {
  initial_contact: 'badge-blue',
  in_process: 'badge-gold',
  documents_submitted: 'badge-yellow',
  waiting_approval: 'badge-gray',
  approved_for_registration: 'badge-green',
  rejected: 'badge-red',
};

export default function Prospects() {
  const { t, lang } = useLang();
  const { userName } = useUser();
  const [prospects, setProspects] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', firstNameAr: '', lastNameAr: '',
    nationalId: '', phone: '', email: '', address: '', city: '',
    dateOfBirth: '', requestedProgram: '', branch: '', academicYear: '2026',
    programNotes: '', source: 'facebook', performedBy: ''
  });

  const loadProspects = () => {
    const params: any = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    if (programFilter) params.program = programFilter;
    getProspects(params).then(res => setProspects(res.data)).catch(() => {});
  };

  useEffect(() => { loadProspects(); }, [search, statusFilter, programFilter]);

  // Quick-add: open the new-prospect modal when navigated with ?new=1
  const location = useLocation();
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') {
      setForm(f => ({ ...f, performedBy: userName }));
      setShowModal(true);
    }
  }, [location.search, userName]);

  const handleAdd = async () => {
    await createProspect(form);
    setShowModal(false);
    setForm({
      firstName: '', lastName: '', firstNameAr: '', lastNameAr: '',
      nationalId: '', phone: '', email: '', address: '', city: '',
      dateOfBirth: '', requestedProgram: '', branch: '', academicYear: '2026',
      programNotes: '', source: 'facebook', performedBy: userName
    });
    loadProspects();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('האם למחוק?')) {
      await deleteProspect(id);
      loadProspects();
    }
  };

  const getName = (p: any) => {
    if (lang === 'ar' && p.firstNameAr) return `${p.firstNameAr} ${p.lastNameAr}`;
    return `${p.firstName} ${p.lastName}`;
  };

  const getStatusText = (status: string) => t(`prospectStatuses.${status}`);
  const getSourceText = (source: string) => t(`sources.${source}`);

  const handleExportExcel = () => {
    const headers = [t('name'), t('nationalId'), t('phone'), t('email'), t('city'), t('requestedProgram'), t('source'), t('prospectEntryDate'), t('status')];
    const rows = prospects.map(p => [
      getName(p), p.nationalId || '', p.phone, p.email, p.city || '',
      p.requestedProgram || '', getSourceText(p.source), p.entryDate || '', getStatusText(p.status)
    ]);
    const csvContent = '\uFEFF' + [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'prospects.csv';
    link.click();
  };

  // Get unique programs for filter
  const uniquePrograms = Array.from(new Set(prospects.map(p => p.requestedProgram).filter(Boolean)));

  return (
    <div>
      <div className="page-title">
        <span className="page-title-text">📋 {t('prospectsList')}</span>
        <button className="btn btn-primary" onClick={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}>
          + {t('addProspect')}
        </button>
      </div>

      <div className="page-section">
        <div className="toolbar">
          <input
            className="search-input"
            placeholder={t('search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="form-select"
            style={{ width: 'auto', minWidth: 150 }}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">{t('all')} - {t('status')}</option>
            <option value="initial_contact">{t('prospectStatuses.initial_contact')}</option>
            <option value="in_process">{t('prospectStatuses.in_process')}</option>
            <option value="documents_submitted">{t('prospectStatuses.documents_submitted')}</option>
            <option value="waiting_approval">{t('prospectStatuses.waiting_approval')}</option>
            <option value="approved_for_registration">{t('prospectStatuses.approved_for_registration')}</option>
            <option value="rejected">{t('prospectStatuses.rejected')}</option>
          </select>
          <select
            className="form-select"
            style={{ width: 'auto', minWidth: 150 }}
            value={programFilter}
            onChange={e => setProgramFilter(e.target.value)}
          >
            <option value="">{t('all')} - {t('requestedProgram')}</option>
            {uniquePrograms.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <button className="btn btn-outline" onClick={handleExportExcel}>{t('exportExcel')} 📥</button>
          <button
            className="btn"
            style={{ backgroundColor: '#B71C1C', color: '#fff', border: 'none' }}
            onClick={() => {
              const headers = ['#', t('name'), t('nationalId'), t('phone'), t('email'), t('city'), t('requestedProgram'), t('source'), t('prospectEntryDate'), t('status')];
              const rows = prospects.map((p, i) => [
                String(i + 1), getName(p), p.nationalId || '-', p.phone, p.email,
                p.city || '-', p.requestedProgram || '-', getSourceText(p.source),
                p.entryDate || '-', getStatusText(p.status)
              ]);
              const date = new Date().toISOString().split('T')[0];
              exportTableToPDF(t('prospectsList'), headers, rows, `prospects_${date}.pdf`);
            }}
          >{t('exportPDF')} 📄</button>
        </div>

        <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('name')}</th>
              <th>{t('nationalId')}</th>
              <th>{t('phone')}</th>
              <th>{t('email')}</th>
              <th>{t('city')}</th>
              <th>{t('requestedProgram')}</th>
              <th>{t('source')}</th>
              <th>{t('prospectEntryDate')}</th>
              <th>{t('status')}</th>
              <th>{t('performedBy')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {prospects.map((p, i) => (
              <tr key={p.id}>
                <td>{i + 1}</td>
                <td>
                  <Link to={`/prospects/${p.id}`} style={{ color: '#7B1FA2', fontWeight: 600, textDecoration: 'none' }}>
                    {getName(p)}
                  </Link>
                </td>
                <td>{p.nationalId || '-'}</td>
                <td dir="ltr">{p.phone}</td>
                <td dir="ltr">{p.email}</td>
                <td>{p.city || '-'}</td>
                <td>{p.requestedProgram || '-'}</td>
                <td>{getSourceText(p.source)}</td>
                <td>{p.entryDate || '-'}</td>
                <td><span className={`badge ${statusColors[p.status] || 'badge-gray'}`}>{getStatusText(p.status)}</span></td>
                <td>{p.performedBy || '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Link to={`/prospects/${p.id}`} className="btn btn-sm btn-outline">{t('details')}</Link>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p.id)}>{t('delete')}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {prospects.length === 0 && (
          <EmptyState
            icon="📋"
            title={t('emptyProspects')}
            actionLabel={t('addProspect')}
            onAction={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}
          />
        )}
      </div>

      {/* Add Prospect Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('addProspect')}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('firstName')} (עברית)</label>
                <input className="form-input" value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('lastName')} (עברית)</label>
                <input className="form-input" value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('firstName')} (ערבית)</label>
                <input className="form-input" value={form.firstNameAr} onChange={e => setForm({...form, firstNameAr: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('lastName')} (ערבית)</label>
                <input className="form-input" value={form.lastNameAr} onChange={e => setForm({...form, lastNameAr: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('nationalId')}</label>
                <input className="form-input" value={form.nationalId} onChange={e => setForm({...form, nationalId: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('phone')}</label>
                <input className="form-input" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('email')}</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('city')}</label>
                <input className="form-input" value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('requestedProgram')}</label>
                <input className="form-input" value={form.requestedProgram} onChange={e => setForm({...form, requestedProgram: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('source')}</label>
                <select className="form-select" value={form.source} onChange={e => setForm({...form, source: e.target.value})}>
                  <option value="facebook">{t('sources.facebook')}</option>
                  <option value="website">{t('sources.website')}</option>
                  <option value="referral">{t('sources.referral')}</option>
                  <option value="fair">{t('sources.fair')}</option>
                  <option value="whatsapp">{t('sources.whatsapp')}</option>
                  <option value="other">{t('sources.other')}</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('branch')}</label>
                <input className="form-input" value={form.branch} onChange={e => setForm({...form, branch: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('academicYear')}</label>
                <input className="form-input" value={form.academicYear} onChange={e => setForm({...form, academicYear: e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('programNotes')}</label>
              <textarea className="form-textarea" value={form.programNotes} onChange={e => setForm({...form, programNotes: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('actionPerformer')}</label>
              <input className="form-input" value={form.performedBy} onChange={e => setForm({...form, performedBy: e.target.value})} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleAdd}>{t('save')}</button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
